import { forwardRef } from "react";
import { FieldValues } from "react-hook-form";
import styled from "@emotion/styled";

import Button, { ButtonButtonProps } from "components/ui/button/Button";
import Input, { InputCoerced, InputProps } from "components/ui/input/Input";
import InputNumber, { InputNumberCoerced, InputNumberProps } from "components/ui/input/InputNumber";

type InputGroupProps = React.ComponentPropsWithoutRef<"div">;

export const InputGroup = forwardRef<HTMLDivElement, InputGroupProps>(
  ({ children, ...props }, ref) => {
    return (
      <StyledInputGroup ref={ref} data-slot="input-group" {...props}>
        {children}
      </StyledInputGroup>
    );
  },
);

InputGroup.displayName = "InputGroup";

const StyledInputGroup = styled.div(({ theme }) => ({
  position: "relative",
  display: "flex",
  width: "100%",
  minWidth: 0,
  alignItems: "center",
  height: 32,
  borderRadius: 4,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  backgroundColor: theme.colors.sysBackgroundDefault,
  transition: "border-color 0.2s, outline 0.2s, background-color 0.2s",

  "&:hover:not(:has([data-slot='input-group-control']:disabled)):not(:has([data-slot='input-group-control']:user-invalid))":
    {
      borderColor: theme.colors.sysPrimaryDefault,
    },

  "&:has([data-slot='input-group-control']:focus)": {
    borderColor: theme.colors.sysPrimaryDefault,
    outline: `2px solid ${theme.colors.sysPrimaryDefault}1A`,
    outlineOffset: 0,
  },

  "&:has([data-slot='input-group-control']:user-invalid)": {
    borderColor: theme.colors.sysDestructiveDefault,
    "&:has([data-slot='input-group-control']:focus)": {
      outline: `2px solid ${theme.colors.sysDestructiveDefault}1A`,
      outlineOffset: 0,
    },
  },

  "&:has([data-slot='input-group-control']:disabled)": {
    backgroundColor: theme.colors.sysBackgroundInactive,
    borderColor: theme.colors.sysBackgroundInactive,
    cursor: "not-allowed",
  },

  "&:has([data-align='block-start']), &:has([data-align='block-end'])": {
    flexDirection: "column",
    height: "auto",
  },
}));

type AddonAlign = "inline-start" | "inline-end" | "block-start" | "block-end";

type InputGroupAddonProps = React.ComponentPropsWithoutRef<"div"> & {
  align?: AddonAlign;
};

export const InputGroupAddon = forwardRef<HTMLDivElement, InputGroupAddonProps>(
  ({ children, align = "inline-start", onClick, ...props }, ref) => {
    /* This is made so clicking anywhere on the InputGroupAddon (the wrapper) automatically shifts the cursor focus to the actual <input>
    And to avoid overwriting onClick behaviour via props */
    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement | null;
      if (target && target.closest("button, a")) {
        return;
      }
      e.currentTarget.parentElement?.querySelector("input")?.focus();
      onClick?.(e);
    };

    return (
      <StyledAddon ref={ref} data-align={align} onClick={handleClick} {...props}>
        {children}
      </StyledAddon>
    );
  },
);

InputGroupAddon.displayName = "InputGroupAddon";

const StyledAddon = styled.div<{ "data-align": AddonAlign }>(({ theme }) => ({
  display: "flex",
  flexShrink: 0,
  alignItems: "center",
  gap: 8,
  color: theme.colors.sysTextSecondary,
  cursor: "text",
  userSelect: "none",
  "&[data-align=inline-start] > svg": {
    paddingLeft: 8,
  },
  "&[data-align=inline-end] > svg": {
    paddingRight: 8,
  },

  "&[data-align=block-start]": {
    width: "100%",
    justifyContent: "flex-start",
    boxSizing: "border-box", // Keep content from overflowing input
    "&:not(:has(button))": {
      padding: "5px 8px",
    },
  },

  "&[data-align=block-end]": {
    width: "100%",
    justifyContent: "flex-start",
    boxSizing: "border-box", // Keep content from overflowing input
    "&:not(:has(button))": {
      padding: "5px 8px",
    },
  },
}));

export const InputGroupButton = forwardRef<HTMLButtonElement, ButtonButtonProps>(
  ({ type = "button", buttonType = "primary", buttonStyle = "inverse", ...props }, ref) => {
    return (
      <StyledAddonButton
        ref={ref}
        type={type}
        buttonType={buttonType}
        buttonStyle={buttonStyle}
        {...props}
      />
    );
  },
);

InputGroupButton.displayName = "InputGroupButton";

const StyledAddonButton = styled(Button)({
  height: 30, // 2px less due to border
  "& svg": {
    width: 14,
    height: 14,
  },
});

const groupInputStyles = {
  border: "none",
  outline: "none",
  backgroundColor: "transparent",
  height: "auto",
  transition: "none",
  "&:hover": {
    border: "none",
  },
  "&:focus": {
    border: "none",
    outline: "none",
  },
  "&:user-invalid": {
    border: "none",
    outline: "none",
    "&:focus": {
      border: "none",
      outline: "none",
    },
  },
};

const StyledGroupInput = styled(Input)(groupInputStyles);
export const InputGroupInput = forwardRef<HTMLInputElement, InputProps>(function InputGroupInput<
  TFieldValues extends FieldValues = FieldValues,
>({ ...props }: InputProps<TFieldValues>, externalRef: React.ForwardedRef<HTMLInputElement>) {
  return (
    <StyledGroupInput
      ref={externalRef}
      data-slot="input-group-control"
      {...(props as InputProps<FieldValues>)}
    />
  );
}) as InputCoerced; // Needed for generics and useForm type error

const StyledGroupInputNumber = styled(InputNumber)(groupInputStyles);
export const InputGroupInputNumber = forwardRef<HTMLInputElement, InputNumberProps>(
  function InputGroupInputNumber<TFieldValues extends FieldValues = FieldValues>(
    { ...props }: InputNumberProps<TFieldValues>,
    externalRef: React.ForwardedRef<HTMLInputElement>,
  ) {
    return (
      <StyledGroupInputNumber
        ref={externalRef}
        data-slot="input-group-control"
        {...(props as InputNumberProps<FieldValues>)}
      />
    );
  },
) as InputNumberCoerced; // Needed for generics and useForm type error
